import React, { useEffect, useState } from 'react';
import FlippexLoader from './components/ui/FlippexLoader';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, Outlet } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import '@/App.css';

import ScrollToTop from './components/ScrollToTop';
import HomePage from './pages/HomePage';
import HowItWorksPage from './pages/HowItWorksPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import FlippexPlusPage from './pages/FlippexPlusPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FaqsPage from './pages/FaqsPage';
import LoaderPreviewPage from './pages/LoaderPreviewPage';
import LegalPage from './pages/LegalPage';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import CookiesPage from './pages/legal/CookiesPage';
import RiskPage from './pages/legal/RiskPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import PortfolioPage from './pages/PortfolioPage';
import ActivityPage from './pages/ActivityPage';
import InvestmentDetailPage from './pages/InvestmentDetailPage';
import TransactionsPage from './pages/TransactionsPage';
import NotificationsPage from './pages/NotificationsPage';
import SettingsPage from './pages/SettingsPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminOverviewPage from './pages/admin/AdminOverviewPage';
import AdminTasksPage from './pages/admin/AdminTasksPage';
import AdminShell from './components/admin/shell/AdminShell';
import AdminPartiesPage from './pages/admin/AdminPartiesPage';
import AdminProjectsDashboardPage from './pages/admin/AdminProjectsDashboardPage';
import AdminV2ProjectDetailPage from './pages/admin/AdminV2ProjectDetailPage';
import AdminDistributionCalculatorPage from './pages/admin/AdminDistributionCalculatorPage';
import AdminDistributionWizardPage from './pages/admin/AdminDistributionWizardPage';
import AdminFinanceReportsPage from './pages/admin/AdminFinanceReportsPage';
import AdminInstrumentsPage from './pages/admin/AdminInstrumentsPage';
import AdminCapTablePage from './pages/admin/AdminCapTablePage';
import AdminFlippexPortfolioPage from './pages/admin/AdminFlippexPortfolioPage';
import AdminInvestorProfilePage from './pages/admin/AdminInvestorProfilePage';
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage';
import AdminPostsPage from './pages/admin/AdminPostsPage';
import AdminRecognitionPage from './pages/admin/AdminRecognitionPage';
import AdminTeamPage from './pages/admin/AdminTeamPage';
import LoginPage from './pages/LoginPage';
import InstrumentWizardPage from './pages/admin/InstrumentWizardPage';
import ForInvestorsPage from './pages/ForInvestorsPage';
import CompliancePage from './pages/CompliancePage';
import SubmitProjectPage from './pages/SubmitProjectPage';
import SponsorDashboardPage from './pages/SponsorDashboardPage';
import NewsPage from './pages/NewsPage';
import PostDetailPage from './pages/PostDetailPage';
import MarketplacePage from './pages/MarketplacePage';

import { AuthProvider, useAuth } from './context/AuthContext';
import AppShell from './components/app/AppShell';

/**
 * Protected Route for Investors Only
 * Redirects admin users to /admin
 */
const InvestorRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <FlippexLoader size={64} animate={false} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Admin users should not see investor pages
  if (user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

/**
 * Protected Route for Admin Only
 * Redirects non-admin users to /dashboard with toast
 */
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <FlippexLoader size={64} animate={false} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (user.role !== 'admin') {
    toast.error('Unauthorized: Admin access required');
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * Legacy ProtectedRoute for backwards compatibility
 */
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <FlippexLoader size={64} animate={false} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AdminRouteShell = ({ children }) => (
  <AdminRoute>
    <AdminShell>{children}</AdminShell>
  </AdminRoute>
);

/**
 * Authenticated investor App layout ("My Flippex").
 * Persistent app shell (header + bottom navigation) shared by all /app routes.
 */
const AppLayout = ({ hideTabBar = false }) => (
  <InvestorRoute>
    <AppShell hideTabBar={hideTabBar}>
      <Outlet />
    </AppShell>
  </InvestorRoute>
);

const RedirectToAppInvestment = () => {
  const { securityId } = useParams();
  return <Navigate to={`/app/portfolio/${securityId}`} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="App min-h-screen">
          <Routes>
            {/* Public Pages */}
            <Route path="/" element={<HomePage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/for-investors" element={<ForInvestorsPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/submit-project" element={<SubmitProjectPage />} />
            <Route path="/sponsor/dashboard" element={<SponsorDashboardPage />} />
            <Route path="/plus" element={<FlippexPlusPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faqs" element={<FaqsPage />} />
            <Route path="/dev/loader" element={<LoaderPreviewPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:slug" element={<PostDetailPage />} />
            <Route path="/learn" element={<Navigate to="/news" replace />} />
            <Route path="/learn/:slug" element={<Navigate to="/news" replace />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            
            {/* Legal Pages */}
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/legal/cookies" element={<CookiesPage />} />
            <Route path="/legal/risk" element={<RiskPage />} />
            
            {/* Flippex App - authenticated investor experience ("My Flippex") */}
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<Navigate to="/app/home" replace />} />
              <Route path="home" element={<DashboardPage />} />
              <Route path="portfolio" element={<PortfolioPage />} />
              <Route path="portfolio/:securityId" element={<InvestmentDetailPage />} />
              <Route path="explore" element={<ProjectsPage context="app" />} />
              <Route path="explore/:id" element={<ProjectDetailPage context="app" />} />
              <Route path="activity" element={<ActivityPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            {/* Focused flows keep the app shell but hide the bottom navigation */}
            <Route path="/app" element={<AppLayout hideTabBar />}>
              <Route path="onboarding" element={<OnboardingPage embedded />} />
            </Route>

            {/* Legacy investor routes -> App */}
            <Route path="/dashboard" element={<Navigate to="/app/home" replace />} />
            <Route path="/portfolio" element={<Navigate to="/app/portfolio" replace />} />
            <Route path="/activity" element={<Navigate to="/app/activity" replace />} />
            <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
            <Route path="/onboarding" element={<Navigate to="/app/onboarding" replace />} />
            <Route path="/investments/:securityId" element={<RedirectToAppInvestment />} />

            {/* Protected Investor Pages - Admin users redirected to /admin */}
            <Route
              path="/transactions"
              element={
                <InvestorRoute>
                  <TransactionsPage />
                </InvestorRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <InvestorRoute>
                  <NotificationsPage />
                </InvestorRoute>
              }
            />
            
            {/* Admin Pages - Non-admin users redirected to /dashboard */}
            <Route
              path="/admin"
              element={
                <AdminRouteShell>
                  <AdminOverviewPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/tasks"
              element={
                <AdminRouteShell>
                  <AdminTasksPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/tools"
              element={
                <AdminRouteShell>
                  <AdminDashboardPage />
                </AdminRouteShell>
              }
            />
            {/* V2 Core Routes */}
            <Route
              path="/admin/v2/parties"
              element={
                <AdminRouteShell>
                  <AdminPartiesPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/v2/projects"
              element={
                <AdminRouteShell>
                  <AdminProjectsDashboardPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/v2/projects/:projectId"
              element={
                <AdminRouteShell>
                  <AdminV2ProjectDetailPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/v2/subscriptions"
              element={
                <AdminRouteShell>
                  <AdminSubscriptionsPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/v2/distributions/:distributionId"
              element={
                <AdminRouteShell>
                  <AdminDistributionWizardPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/v2/distributions"
              element={
                <AdminRouteShell>
                  <AdminDistributionCalculatorPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/v2/finance"
              element={
                <AdminRouteShell>
                  <AdminFinanceReportsPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/instruments"
              element={
                <AdminRouteShell>
                  <AdminInstrumentsPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/instruments/new"
              element={
                <AdminRouteShell>
                  <InstrumentWizardPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/captable/:instrumentId"
              element={
                <AdminRouteShell>
                  <AdminCapTablePage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/flippex-portfolio"
              element={
                <AdminRouteShell>
                  <AdminFlippexPortfolioPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/investor/:investorId"
              element={
                <AdminRouteShell>
                  <AdminInvestorProfilePage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/news"
              element={
                <AdminRouteShell>
                  <AdminPostsPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/recognition"
              element={
                <AdminRouteShell>
                  <AdminRecognitionPage />
                </AdminRouteShell>
              }
            />
            <Route
              path="/admin/team"
              element={
                <AdminRouteShell>
                  <AdminTeamPage />
                </AdminRouteShell>
              }
            />
            <Route path="/admin/articles" element={<Navigate to="/admin/news" replace />} />
            {/* Legacy V1 routes - redirect to V2 equivalents */}
            <Route path="/admin/investors" element={<Navigate to="/admin/v2/parties" replace />} />
            <Route path="/admin/investors/:investorId" element={<Navigate to="/admin/v2/parties" replace />} />
            <Route path="/admin/projects" element={<Navigate to="/admin/v2/projects" replace />} />
            <Route path="/admin/projects/:projectId" element={<Navigate to="/admin/v2/projects" replace />} />
            <Route path="/admin/projects/:projectId/captable" element={<Navigate to="/admin/v2/projects" replace />} />
            <Route path="/admin/projects/:projectId/financials" element={<Navigate to="/admin/v2/projects" replace />} />
            <Route path="/admin/distributions" element={<Navigate to="/admin/v2/distributions" replace />} />
            <Route path="/admin/commitments" element={<Navigate to="/admin/v2/subscriptions" replace />} />
            <Route path="/admin/transactions" element={<Navigate to="/admin/v2/finance" replace />} />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
